import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { PermissionsHttpClient } from '@omnea-app/permissions/permissionsHttpClient'
import { RecordPermissionsResponseError } from './errors'
import type { AskedRecord } from './askedRecord'
import type { RecordPermissionsEntity, RecordPermissionsFor } from '@omnea-digital/authz-model'

export type ResolvedRecordAuthorization<Entity extends RecordPermissionsEntity> = {
  entity: Entity
  status: 'resolved'
  permissions: RecordPermissionsFor<Entity>
}

const isForEntity = <Entity extends RecordPermissionsEntity>(
  answer: { entity: RecordPermissionsEntity; permissions: unknown },
  entity: Entity
): answer is { entity: Entity; permissions: RecordPermissionsFor<Entity> } => answer.entity === entity

/** Every permission the caller holds on one record, as a detail page renders from. */
export const fetchRecordAuthorization = async <Entity extends RecordPermissionsEntity>(
  getToken: () => Promise<string>,
  asked: AskedRecord<Entity>
): Promise<ResolvedRecordAuthorization<Entity>> => {
  const permissionsClient = new PermissionsHttpClient({ authorization: await getToken() })
  const { data: response, error } = await permissionsClient.GET('/entities/{entity}/{recordId}/permissions', {
    params: { path: asked },
  })
  throwIfApiError(error)
  if (response == null) throw new RecordPermissionsResponseError({ reason: 'noBody', ...asked })
  const answer = response.recordPermissions
  if (!isForEntity(answer, asked.entity)) {
    throw new RecordPermissionsResponseError({ reason: 'answeredForAnotherEntity', ...asked, answeredFor: answer.entity })
  }
  return { entity: answer.entity, status: 'resolved', permissions: answer.permissions }
}
